import { useCallback, useEffect, useRef, useState } from 'react';
import { Label, ListBox, ListBoxItem, Select } from '@heroui/react';

import TaskBoard from '../tasks/TaskBoard';
import TaskDialog from '../tasks/TaskDialog';
import Notice from '../../common/Notice';
import tasksService from '../../../services/tasks.service';
import authService from '../../../services/auth.service';
import { getUser } from '../../../services/session';
import { TASKS, messageOf, SELECT_PLACEHOLDER } from '../../../i18n/el';

// «Καθήκοντα»: **τα καθήκοντα σε όλες τις συλλογές που βλέπει ο χρήστης**.
//
// **Τι υπήρχε εδώ, και γιατί έπρεπε να φύγει.** Η οθόνη έλεγε ρητά «δεν έχει
// υλοποιηθεί ακόμη», με παραπομπή στη σελίδα της συλλογής. Όσο δεν υπήρχαν
// καθήκοντα πουθενά, ήταν αληθές. Με το P1-4 υπάρχουν, οπότε η ίδια οθόνη έγινε
// **ορατό ψέμα**: ο πρώτος που θα την άνοιγε θα συμπέραινε ότι το P1-4 δεν
// δουλεύει. Ο ίδιος ο κώδικας το είχε δηλώσει, μαζί με το τι λείπει — μια
// διαδρομή `GET /api/tasks` εκτός του πίνακα του §4.5.1. Η **Απόφαση D29** την
// πήρε ρητά, με το προηγούμενο της διασυλλογικής αναζήτησης πόρων.
//
// **Ο τίτλος είναι «Καθήκοντα», ο ίδιος με το πλαϊνό μενού.** Έλεγε «Τα
// καθήκοντά μου» ενώ το μενού έλεγε «Καθήκοντα»: δύο ονόματα για το ίδιο πράγμα,
// με μία μετάβαση ανάμεσά τους — η ίδια απόκλιση που βρέθηκε στα κλειδιά
// πρόσβασης, και ελέγχεται πλέον με τον ίδιο τρόπο. Ο ελληνικός όρος είναι και
// **ακριβέστερος**: το εύρος της οθόνης είναι οι ορατές συλλογές, όχι μόνο όσα
// έχουν ανατεθεί στον συνδεδεμένο.
//
// **Το άνοιγμα ενός καθηκόντος οδηγεί στη συλλογή του**, στην καρτέλα των
// καθηκόντων, και δεν ανοίγει διάλογο εδώ. Δεν είναι έλλειψη: ο διάλογος
// χρειάζεται τα **μέλη** της συλλογής για τους αναδόχους, και ο κατάλογος αυτής
// της οθόνης δεν τα φέρνει — σκόπιμα, γιατί θα σήμαινε να ταξιδεύουν τα μέλη
// κάθε ορατής συλλογής σε κάθε φόρτωση. Διάλογος με άδειο κατάλογο αναδόχων θα
// ήταν σιωπηλή υποβάθμιση: ο συντονιστής θα έβλεπε φόρμα ανάθεσης χωρίς κανέναν
// να αναθέσει.

/** Η τιμή του επιλογέα όταν δεν φιλτράρει καμία συλλογή. */
const ALL = 'all';

export default function ToDoAppPage() {
    const [tasks, setTasks] = useState([]);
    const [collections, setCollections] = useState([]);
    const [selected, setSelected] = useState(ALL);
    const [error, setError] = useState('');
    const [isLoading, setLoading] = useState(true);

    // Το καθήκον που ανοίγει, και το **πλαίσιο της συλλογής του**: μέλη για τους
    // αναδόχους, πόροι για τον σχετικό πόρο. Χωριστές καταστάσεις, γιατί το πρώτο
    // το ξέρουμε αμέσως και το δεύτερο θέλει αιτήματα.
    const [editing, setEditing] = useState(null);
    const [context, setContext] = useState(null);
    const [isContextLoading, setContextLoading] = useState(false);

    /**
     * **Το πλαίσιο κάθε συλλογής ζητείται μία φορά ανά συνεδρία της οθόνης.**
     *
     * Ο κατάλογος καθηκόντων είναι διασυλλογικός, οπότε το άνοιγμα δέκα
     * καθηκόντων της ίδιας ομάδας θα πλήρωνε δέκα φορές το ίδιο ζεύγος
     * αιτημάτων. Δεν είναι γενική μνήμη: ζει όσο η σελίδα, και κάθε νέα είσοδος
     * στην οθόνη τη βρίσκει άδεια.
     */
    const contextCache = useRef(new Map());

    const currentUserId = getUser()?.id;

    const load = useCallback(() => {
        setLoading(true);

        tasksService.getVisibleTasks({ collection: selected === ALL ? '' : selected }).then(
            (page) => {
                // **Δεν εμπιστεύεται τη μορφή**, όπως και η κάρτα της συλλογής:
                // απόντα πεδία σε απόκριση που δεν έφτασε ως απόρριψη είναι
                // κατάσταση που η προεπιλογή παραμέτρου δεν πιάνει.
                setTasks(Array.isArray(page?.data) ? page.data : []);
                setError('');
                setLoading(false);

                // Ο κατάλογος του επιλογέα ανανεώνεται **μόνο** από την
                // αφιλτράριστη ανάγνωση. Αλλιώς, μόλις ο χρήστης επιλέξει μία
                // συλλογή, ο κατάλογος θα συρρικνωνόταν σε εκείνη και δεν θα
                // υπήρχε τρόπος να επιστρέψει — φίλτρο που τρώει τον εαυτό του.
                if (selected === ALL) {
                    setCollections(Array.isArray(page?.meta?.collections) ? page.meta.collections : []);
                }
            },
            (apiError) => {
                setError(messageOf(apiError));
                setLoading(false);
            }
        );
    }, [selected]);

    useEffect(load, [load]);

    function handleCollection(key) {
        setSelected(String(key));
    }

    function dismissError() {
        setError('');
    }

    /**
     * Ο ρόλος **ανά συλλογή**, από το `meta` της ίδιας απόκρισης.
     *
     * Εδώ, σε αντίθεση με τη σελίδα μιας συλλογής, κάθε καθήκον μπορεί να ανήκει
     * σε άλλη ομάδα: μια σταθερή σημαία θα έδινε σε όλα τον ρόλο του ενός.
     */
    function canManageOf(task) {
        const id = String(task.collection?.id ?? task.collection ?? '');
        const entry = collections.find((candidate) => candidate.id === id);
        return entry?.role === 'coordinator' || entry?.role === 'admin';
    }

    function collectionNameOf(task) {
        const id = String(task.collection?.id ?? task.collection ?? '');
        return collections.find((candidate) => candidate.id === id)?.title ?? null;
    }

    /** Η **μία** διαδρομή αλλαγής κατάστασης, ίδια με εκείνη της συλλογής. */
    function changeStatus(task, status) {
        return tasksService.updateTask(task.id, { status }).then(
            load,
            (apiError) => Promise.reject(new Error(messageOf(apiError)))
        );
    }

    /**
     * Το άνοιγμα δίνει τον **ίδιο διάλογο** με τη σελίδα της συλλογής.
     *
     * Πριν το Σετ Β η ενέργεια ήταν **πλοήγηση**: ο χρήστης έβγαινε από τη
     * διασυλλογική οθόνη και ξανάβρισκε το καθήκον σε άλλη σελίδα, όπου η
     * επιστροφή τον έφερνε πίσω χωρίς τη θέση του. Η διασυλλογική οθόνη υπάρχει
     * ακριβώς για να **μη** χρειάζεται να μπει κανείς σε κάθε συλλογή χωριστά
     * (ΛΑ-20)· διαδρομή που καταλήγει σε πλοήγηση την ακυρώνει.
     *
     * ⚠ **Το κόστος είναι δύο αιτήματα, και μόνο στο πρώτο άνοιγμα ανά συλλογή.**
     * Ο διάλογος χρειάζεται δύο πράγματα που ο κατάλογος καθηκόντων δεν φέρνει
     * και δεν πρέπει να φέρνει: τα **μέλη** της συλλογής, που ζουν στο έγγραφο
     * της συλλογής, και τους **πόρους** της. Το δεύτερο άνοιγμα καθηκόντος της
     * ίδιας συλλογής κοστίζει μηδέν.
     *
     * **Ο διάλογος δεν ανοίγει πριν φτάσουν και τα δύο**, και η αναμονή είναι
     * ορατή. Η φθηνή εναλλακτική —άνοιγμα με κενούς καταλόγους που γεμίζουν
     * μετά— θα έδειχνε «κανένα μέλος» και «κανένας πόρος» ως **απάντηση**: ο
     * χρήστης δεν έχει τρόπο να ξεχωρίσει τον άδειο κατάλογο από τον κατάλογο
     * που δεν έχει έρθει ακόμη, και το πιθανότερο συμπέρασμα είναι ότι η
     * συλλογή δεν έχει μέλη.
     */
    function openTask(task) {
        const id = String(task.collection?.id ?? task.collection ?? '');
        if (!id) return;

        setError('');

        const cached = contextCache.current.get(id);
        if (cached) {
            setContext(cached);
            setEditing(task);
            return;
        }

        setContextLoading(true);
        Promise.all([
            authService.getCollection(id),
            authService.getCollectionResources(id)
        ]).then(
            ([collection, page]) => {
                const loaded = {
                    collectionId: id,
                    collection,
                    resources: Array.isArray(page?.data) ? page.data : []
                };
                contextCache.current.set(id, loaded);
                setContext(loaded);
                setEditing(task);
                setContextLoading(false);
            },
            (apiError) => {
                setError(messageOf(apiError));
                setContextLoading(false);
            }
        );
    }

    function closeDialog() {
        setEditing(null);
        setContext(null);
    }

    /**
     * Μετά την αποθήκευση **το πλαίσιο πετιέται** μαζί με τον διάλογο.
     *
     * Η συλλογή μπορεί να άλλαξε στο μεταξύ από την ίδια την ενέργεια, και ο
     * κατάλογος των καθηκόντων ξαναφορτώνεται· κρατημένο πλαίσιο θα ήταν το μόνο
     * κομμάτι της οθόνης που δεν ανανεώθηκε.
     */
    function handleSaved() {
        contextCache.current.delete(context?.collectionId ?? '');
        closeDialog();
        load();
    }

    const options = [{ id: ALL, title: TASKS.allCollections }, ...collections];

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-xl font-bold">{TASKS.title}</h2>
                <p className="text-sm text-muted">{TASKS.crossIntro}</p>
            </div>

            {/* Το σφάλμα **δίπλα στην ενέργεια**, μέσα στη σελίδα και πάνω από τον
                πίνακα που αφορά — όχι σε θέση που μπορεί να βρεθεί εκτός οπτικού
                πεδίου όταν ο χρήστης πατά κάτι χαμηλά στον πίνακα. */}
            {error && (
                <Notice status="warning" title={TASKS.title} onDismiss={dismissError}>
                    {error}
                </Notice>
            )}

            {/* Ο επιλογέας γεμίζει από τον **ίδιο** κατάλογο που παρήγαγε τα
                δεδομένα, οπότε δεν προσφέρει ποτέ συλλογή που ο χρήστης δεν
                βλέπει — ούτε συλλογή χωρίς καθήκοντα, που θα έδινε άδειο πίνακα
                χωρίς εξήγηση. */}
            <Select placeholder={SELECT_PLACEHOLDER}
                selectedKey={selected}
                onSelectionChange={handleCollection}
                className="w-full sm:max-w-xs"
            >
                <Label>{TASKS.collectionFilter}</Label>
                <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox items={options}>
                        {(item) => <ListBoxItem id={item.id}>{item.title}</ListBoxItem>}
                    </ListBox>
                </Select.Popover>
            </Select>

            {/* **Η αναμονή είναι ορατή και ονομάζει τι περιμένει.** Χωρίς αυτό,
                το πάτημα μιας κάρτας δεν θα είχε καμία απόκριση μέχρι να ανοίξει
                ο διάλογος, και η φυσική αντίδραση είναι δεύτερο πάτημα. */}
            {isContextLoading && (
                <Notice status="info" title={TASKS.loadingContext}>
                    {TASKS.loadingContextHint}
                </Notice>
            )}

            {!isLoading && !error && tasks.length === 0 ? (
                <p className="text-sm text-muted">{TASKS.crossEmpty}</p>
            ) : (
                <TaskBoard
                    tasks={tasks}
                    currentUserId={currentUserId}
                    canManageOf={canManageOf}
                    collectionNameOf={collectionNameOf}
                    onOpenTask={openTask}
                    onChangeStatus={changeStatus}
                />
            )}

            {/* ⚠ **Υπό συνθήκη** κατά την D26: το `isOpen` γίνεται ψευδές από
                πηγή εκτός του διαλόγου — εδώ από την αποθήκευση και τη διαγραφή —
                και το `Modal.Root` κλείνει μόνο όταν το κλείσιμο το ξεκινά το
                ίδιο. Η συνθήκη περιλαμβάνει **και** το πλαίσιο: χωρίς αυτό ο
                διάλογος θα αποδιδόταν για μια στιγμή με κενά τα δύο πεδία που
                εξαρτώνται από τα αιτήματα. */}
            {editing && context && (
                <TaskDialog
                    task={editing}
                    collectionId={context.collectionId}
                    collection={context.collection}
                    resources={context.resources}
                    canManage={canManageOf(editing)}
                    currentUserId={currentUserId}
                    onClose={closeDialog}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
}
